import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Edit, CheckCircle, Clock, MapPin, Plane, User, Calendar, Plus, Stethoscope } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { formatDate, CAYMAN_TZ } from '../../utils/dateFormat';
import { logger } from '../../utils/logger';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const ViewRelocation = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const password = sessionStorage.getItem('admin_password');
  const [relocation, setRelocation] = useState(null);
  const [client, setClient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showMilestoneForm, setShowMilestoneForm] = useState(false);
  const [milestoneData, setMilestoneData] = useState({
    status: '',
    notes: '',
    completed_by: 'Admin'
  });

  useEffect(() => {
    if (!password) {
      navigate('/admin/login');
      return;
    }
    fetchRelocation();
  }, [id]);

  const fetchRelocation = async () => {
    try {
      const response = await fetch(`${API_URL}/api/client/relocations/${id}`, {
        headers: { 'Authorization': `Basic ${btoa(':' + password)}` }
      });
      const data = await response.json();
      setRelocation(data);
      
      // Fetch client details
      if (data.client_id) {
        const clientsResponse = await fetch(`${API_URL}/api/admin/clients`, {
          headers: { 'Authorization': `Basic ${btoa(':' + password)}` }
        });
        const clients = await clientsResponse.json();
        const foundClient = clients.find(c => c.id === data.client_id);
        setClient(foundClient);
      }
    } catch (error) {
      logger.error('Error fetching relocation:', error);
      toast.error('Failed to load relocation details');
    } finally {
      setLoading(false);
    }
  };

  const handleAddMilestone = async (e) => {
    e.preventDefault();
    
    try {
      const response = await fetch(`${API_URL}/api/admin/relocations/${id}/milestone`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Basic ${btoa(':' + password)}`
        },
        body: JSON.stringify(milestoneData)
      });

      if (response.ok) {
        toast.success('Status updated successfully!');
        setShowMilestoneForm(false);
        setMilestoneData({ status: '', notes: '', completed_by: 'Admin' });
        fetchRelocation(); // Refresh data
      } else {
        toast.error('Failed to update status');
      }
    } catch (error) {
      logger.error('Error adding milestone:', error);
      toast.error('An error occurred');
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      'quote_sent': 'bg-blue-100 text-blue-800 border-blue-200',
      'booking_confirmed': 'bg-purple-100 text-purple-800 border-purple-200',
      'documents_required': 'bg-yellow-100 text-yellow-800 border-yellow-200',
      'documents_submitted': 'bg-orange-100 text-orange-800 border-orange-200',
      'documents_under_review': 'bg-orange-100 text-orange-800 border-orange-200',
      'documents_approved': 'bg-green-100 text-green-800 border-green-200',
      'travel_arrangements': 'bg-indigo-100 text-indigo-800 border-indigo-200',
      'flight_booked': 'bg-indigo-100 text-indigo-800 border-indigo-200',
      'pre_travel_prep': 'bg-teal-100 text-teal-800 border-teal-200',
      'in_transit': 'bg-pink-100 text-pink-800 border-pink-200',
      'delivered': 'bg-green-100 text-green-800 border-green-200',
      'complete': 'bg-gray-100 text-gray-800 border-gray-200'
    };
    return colors[status] || 'bg-gray-100 text-gray-800 border-gray-200';
  };

  const formatStatus = (status) => {
    return status.split('_').map(word => 
      word.charAt(0).toUpperCase() + word.slice(1)
    ).join(' ');
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return 'Not set';
    return new Date(dateString).toLocaleString('en-US', {
      timeZone: CAYMAN_TZ,
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const statusOptions = [
    { value: 'quote_sent', label: 'Quote Sent' },
    { value: 'booking_confirmed', label: 'Booking Confirmed' },
    { value: 'documents_required', label: 'Documents Required' },
    { value: 'documents_submitted', label: 'Documents Submitted' },
    { value: 'documents_under_review', label: 'Documents Under Review' },
    { value: 'documents_approved', label: 'Documents Approved' },
    { value: 'travel_arrangements', label: 'Travel Arrangements' },
    { value: 'flight_booked', label: 'Flight Booked' },
    { value: 'pre_travel_prep', label: 'Pre-Travel Prep' },
    { value: 'in_transit', label: 'In Transit' },
    { value: 'delivered', label: 'Delivered' },
    { value: 'complete', label: 'Complete' }
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading relocation details...</p>
        </div>
      </div>
    );
  }

  if (!relocation) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-600">Relocation not found</p>
          <Button onClick={() => navigate('/admin/relocations')} className="mt-4">
            Back to Relocations
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex justify-between items-start">
            <div className="flex items-center gap-4">
              <Button
                variant="outline"
                onClick={() => navigate('/admin/relocations')}
                className="flex items-center gap-2"
              >
                <ArrowLeft className="w-4 h-4" />
                Back
              </Button>
              <div>
                <h1 className="text-3xl font-bold text-gray-900">
                  {relocation.pet_name}'s Relocation
                </h1>
                <p className="mt-1 text-sm text-gray-600">
                  {relocation.origin} → {relocation.destination}
                </p>
              </div>
            </div>
            <div className="flex gap-2 flex-wrap">
              <Button
                variant="outline"
                onClick={() => navigate(`/admin/health-certificates/new?relocation_id=${id}`)}
                className="flex items-center gap-2"
                data-testid="start-health-cert-btn"
              >
                <Stethoscope className="w-4 h-4" />
                Start Health Certificate
              </Button>
              <Button
                variant="outline"
                onClick={() => navigate(`/admin/relocations/${id}/edit`)}
                className="flex items-center gap-2"
              >
                <Edit className="w-4 h-4" />
                Edit
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Status Card */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-gray-900">Current Status</h2>
                <span className={`px-4 py-2 rounded-lg border-2 font-semibold ${getStatusColor(relocation.current_status)}`}>
                  {formatStatus(relocation.current_status)}
                </span>
              </div>
              
              <Button
                onClick={() => setShowMilestoneForm(!showMilestoneForm)}
                className="w-full flex items-center justify-center gap-2"
                variant="outline"
              >
                <Plus className="w-4 h-4" />
                Update Status
              </Button>

              {showMilestoneForm && (
                <form onSubmit={handleAddMilestone} className="mt-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        New Status
                      </label>
                      <select
                        value={milestoneData.status}
                        onChange={(e) => setMilestoneData({ ...milestoneData, status: e.target.value })}
                        required
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                      >
                        <option value="">Select status...</option>
                        {statusOptions.map(option => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Notes (optional)
                      </label>
                      <textarea
                        value={milestoneData.notes}
                        onChange={(e) => setMilestoneData({ ...milestoneData, notes: e.target.value })}
                        rows={3}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                        placeholder="Add any notes about this status update..."
                      />
                    </div>

                    <div className="flex gap-2">
                      <Button type="submit" size="sm" className="flex-1">
                        Save Status
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => setShowMilestoneForm(false)}
                      >
                        Cancel
                      </Button>
                    </div>
                  </div>
                </form>
              )}
            </div>

            {/* Pet Information */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Pet Information</h2>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-600">Name</p>
                  <p className="font-medium text-gray-900">{relocation.pet_name}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Type</p>
                  <p className="font-medium text-gray-900">{relocation.pet_type}</p>
                </div>
                {relocation.pet_breed && (
                  <div>
                    <p className="text-sm text-gray-600">Breed</p>
                    <p className="font-medium text-gray-900">{relocation.pet_breed}</p>
                  </div>
                )}
                {relocation.pet_age && (
                  <div>
                    <p className="text-sm text-gray-600">Age</p>
                    <p className="font-medium text-gray-900">{relocation.pet_age}</p>
                  </div>
                )}
                {relocation.pet_weight && (
                  <div>
                    <p className="text-sm text-gray-600">Weight</p>
                    <p className="font-medium text-gray-900">{relocation.pet_weight}</p>
                  </div>
                )}
                {relocation.microchip_number && (
                  <div>
                    <p className="text-sm text-gray-600">Microchip</p>
                    <p className="font-medium text-gray-900">{relocation.microchip_number}</p>
                  </div>
                )}
              </div>
            </div>

            {/* Travel Details */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Travel Details</h2>
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <MapPin className="w-5 h-5 text-gray-400" />
                  <div>
                    <p className="text-sm text-gray-600">Route</p>
                    <p className="font-medium text-gray-900">{relocation.origin} → {relocation.destination}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Calendar className="w-5 h-5 text-gray-400" />
                  <div>
                    <p className="text-sm text-gray-600">Travel Date</p>
                    <p className="font-medium text-gray-900">{formatDate(relocation.travel_date)}</p>
                  </div>
                </div>
                {relocation.arrival_date && (
                  <div className="flex items-center gap-3">
                    <Calendar className="w-5 h-5 text-gray-400" />
                    <div>
                      <p className="text-sm text-gray-600">Arrival Date</p>
                      <p className="font-medium text-gray-900">{formatDate(relocation.arrival_date)}</p>
                    </div>
                  </div>
                )}
                {relocation.airline && (
                  <div className="flex items-center gap-3">
                    <Plane className="w-5 h-5 text-gray-400" />
                    <div>
                      <p className="text-sm text-gray-600">Flight</p>
                      <p className="font-medium text-gray-900">
                        {relocation.airline} {relocation.flight_number && `- ${relocation.flight_number}`}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Notes */}
            {(relocation.special_requirements || relocation.notes) && (
              <div className="bg-white rounded-lg shadow-sm p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">Additional Information</h2>
                {relocation.special_requirements && (
                  <div className="mb-4">
                    <p className="text-sm text-gray-600 mb-1">Special Requirements</p>
                    <p className="text-gray-900 whitespace-pre-wrap">{relocation.special_requirements}</p>
                  </div>
                )}
                {relocation.notes && (
                  <div>
                    <p className="text-sm text-gray-600 mb-1">Internal Notes</p>
                    <p className="text-gray-900 whitespace-pre-wrap">{relocation.notes}</p>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Client Info */}
            {client && (
              <div className="bg-white rounded-lg shadow-sm p-6">
                <div className="flex items-center gap-2 mb-4">
                  <User className="w-5 h-5 text-gray-400" />
                  <h2 className="text-lg font-semibold text-gray-900">Client</h2>
                </div>
                <div className="space-y-2">
                  <div>
                    <p className="text-sm text-gray-600">Name</p>
                    <p className="font-medium text-gray-900">{client.first_name} {client.last_name}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Email</p>
                    <p className="font-medium text-gray-900">{client.email}</p>
                  </div>
                  {client.phone && (
                    <div>
                      <p className="text-sm text-gray-600">Phone</p>
                      <p className="font-medium text-gray-900">{client.phone}</p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Timeline */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <div className="flex items-center gap-2 mb-4">
                <Clock className="w-5 h-5 text-gray-400" />
                <h2 className="text-lg font-semibold text-gray-900">Status Timeline</h2>
              </div>
              <div className="space-y-4">
                {relocation.milestones && relocation.milestones.length > 0 ? (
                  relocation.milestones.slice().reverse().map((milestone, index) => (
                    <div key={index} className="flex gap-3">
                      <div className="flex-shrink-0">
                        <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center">
                          <CheckCircle className="w-4 h-4 text-primary" />
                        </div>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900">
                          {formatStatus(milestone.status)}
                        </p>
                        <p className="text-xs text-gray-500">
                          {formatDateTime(milestone.date_completed)}
                        </p>
                        {milestone.notes && (
                          <p className="text-sm text-gray-600 mt-1">{milestone.notes}</p>
                        )}
                        {milestone.completed_by && (
                          <p className="text-xs text-gray-500 mt-1">by {milestone.completed_by}</p>
                        )}
                      </div>
                    </div>
                  ))
                ) : (
                  <p className="text-sm text-gray-500">No status updates yet</p>
                )}
              </div>
            </div>

            {/* Metadata */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Metadata</h2>
              <div className="space-y-2 text-sm">
                <div>
                  <p className="text-gray-600">Created</p>
                  <p className="font-medium text-gray-900">{formatDateTime(relocation.created_at)}</p>
                </div>
                <div>
                  <p className="text-gray-600">Last Updated</p>
                  <p className="font-medium text-gray-900">{formatDateTime(relocation.updated_at)}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
